import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks";
import { checkRole } from "../recipe_page/recipe_page";
import { API_BASE_URL } from "../../config/env";
import { applyAuthHeaders } from "../../utils/authHeaders";
import { respectCardsAdminPaths } from "../../features/respectCards/api/apiPaths";

type SseEvent =
  | { type: "text_delta"; delta: string }
  | { type: "tool_start"; toolName: string; toolCallId: string }
  | {
    type: "tool_result";
    toolCallId: string;
    toolName: string;
    content: string;
  }
  | { type: "message_end"; fullText: string }
  | { type: "error"; message: string }
  | { type: "done" };

type ChatItem =
  | { kind: "user"; text: string }
  | { kind: "assistant"; text: string; streaming: boolean }
  | {
    kind: "tool";
    toolCallId: string;
    toolName: string;
    content?: string;
    expanded: boolean;
  };

const CHAT_URL = `${API_BASE_URL}${respectCardsAdminPaths.engineChat}`;

async function consumeEngineSse(
  prompt: string,
  onEvent: (ev: SseEvent) => void,
): Promise<void> {
  const headers = new Headers({
    "Content-Type": "application/json",
    Accept: "text/event-stream",
  });
  applyAuthHeaders(headers);

  const response = await fetch(CHAT_URL, {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify({ prompt }),
  });

  if (!response.ok) {
    const text = await response.text().catch(() => response.statusText);
    throw new Error(text || `HTTP ${response.status}`);
  }

  const reader = response.body?.getReader();
  if (!reader) {
    throw new Error("no response body");
  }

  const decoder = new TextDecoder();
  let buffer = "";

  // parse SSE lines: data: {...}\n\n
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const parts = buffer.split("\n\n");
    buffer = parts.pop() ?? "";
    for (const chunk of parts) {
      const line = chunk.split("\n").find((l) => l.startsWith("data: "));
      if (!line) continue;
      const raw = line.slice(6).trim();
      if (!raw) continue;
      try {
        const parsed = JSON.parse(raw) as SseEvent;
        onEvent(parsed);
      } catch {
        // ignore malformed frames
      }
    }
  }
}

const CardEngineChat: React.FC = () => {
  const user = useAppSelector((state) => state.auth);
  const isAdmin = checkRole(user.role ?? "", "admin");
  const streamEndRef = useRef<HTMLDivElement | null>(null);

  const [input, setInput] = useState("");
  const [items, setItems] = useState<ChatItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    streamEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [items, loading]);

  const applySseEvent = useCallback((ev: SseEvent) => {
    if (ev.type === "error") {
      setError(ev.message);
      return;
    }
    setItems((prev) => {
      const next = [...prev];
      switch (ev.type) {
        case "text_delta": {
          const last = next[next.length - 1];
          if (last?.kind === "assistant" && last.streaming) {
            last.text += ev.delta;
          } else {
            next.push({ kind: "assistant", text: ev.delta, streaming: true });
          }
          break;
        }
        case "message_end": {
          const last = next[next.length - 1];
          if (last?.kind === "assistant") {
            last.text = ev.fullText;
            last.streaming = false;
          } else {
            next.push({
              kind: "assistant",
              text: ev.fullText,
              streaming: false,
            });
          }
          break;
        }
        case "tool_start":
          next.push({
            kind: "tool",
            toolCallId: ev.toolCallId,
            toolName: ev.toolName,
            expanded: true,
          });
          break;
        case "tool_result": {
          for (let i = next.length - 1; i >= 0; i--) {
            const it = next[i];
            if (it.kind === "tool" && it.toolCallId === ev.toolCallId) {
              it.content = ev.content;
              break;
            }
          }
          break;
        }
        default:
          break;
      }
      return next;
    });
  }, []);

  const send = async () => {
    const prompt = input.trim();
    if (!prompt || loading) return;

    setInput("");
    setError(null);
    setItems((p) => [...p, { kind: "user", text: prompt }]);
    setLoading(true);

    try {
      await consumeEngineSse(prompt, applySseEvent);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  };

  if (user.bootstrapStatus !== "ready") {
    return null;
  }

  if (!isAdmin) {
    return <Navigate to="/user-stats" replace />;
  }

  return (
    <>
      <style>{`
        @media (max-width: 700px) {
          .cec-root {
            padding: 12px !important;
            max-width: 100% !important;
          }
          .cec-messages {
            padding: 10px !important;
            min-height: 200px !important;
          }
          .cec-input-row {
            flex-direction: column !important;
            align-items: stretch !important;
          }
          .cec-input-row textarea {
            width: 100% !important;
            max-width: 100% !important;
            box-sizing: border-box !important;
            min-width: 0 !important;
          }
          .cec-input-row button {
            align-self: stretch !important;
            width: 100% !important;
          }
          .cec-tool-id {
            display: block !important;
            margin-left: 0 !important;
            margin-top: 4px !important;
            word-break: break-all !important;
          }
        }
      `}</style>
    <div
      className="cec-root"
      style={{
        display: "flex",
        flexDirection: "column",
        minHeight: "100vh",
        maxWidth: 900,
        margin: "0 auto",
        padding: 24,
        background: "#f4f4f4",
        boxSizing: "border-box",
        width: "100%",
      }}
    >
      <h1 style={{ fontSize: "1.4rem", marginBottom: 8 }}>
        Card engine agent
      </h1>
      <p style={{ fontSize: "0.9rem", color: "#555", marginBottom: 16 }}>
        streams tool calls and model output from the respect card sitebot agent. back
        to{" "}
        <Link to="/admin/cards" style={{ color: "#639" }}>
          cards admin
        </Link>
        .
      </p>

      {error && (
        <div
          style={{
            background: "#ffe0e0",
            color: "#800",
            padding: 10,
            borderRadius: 6,
            marginBottom: 12,
          }}
        >
          {error}
        </div>
      )}

      <div
        className="cec-messages"
        style={{
          flex: 1,
          overflowY: "auto",
          background: "#fff",
          borderRadius: 8,
          padding: 16,
          marginBottom: 12,
          border: "1px solid #ddd",
          minHeight: 320,
          maxWidth: "100%",
          boxSizing: "border-box",
        }}
      >
        {items.length === 0 && (
          <p style={{ color: "#888" }}>send a prompt to start the agent.</p>
        )}

        {items.map((item, idx) => {
          if (item.kind === "user") {
            return (
              <div
                key={`u-${idx}`}
                style={{
                  marginBottom: 14,
                  padding: 12,
                  background: "#e8f4ff",
                  borderRadius: 6,
                  whiteSpace: "pre-wrap",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#357", marginBottom: 4 }}>
                  you
                </div>
                {item.text}
              </div>
            );
          }

          if (item.kind === "assistant") {
            return (
              <div
                key={`a-${idx}`}
                style={{
                  marginBottom: 14,
                  padding: 12,
                  background: "#f8f8f8",
                  borderRadius: 6,
                  whiteSpace: "pre-wrap",
                }}
              >
                <div style={{ fontSize: "0.75rem", color: "#555", marginBottom: 4 }}>
                  assistant
                  {item.streaming ? " · streaming…" : ""}
                </div>
                {item.text || (item.streaming ? "…" : "")}
              </div>
            );
          }

          return (
            <div
              key={`t-${item.toolCallId}-${idx}`}
              style={{
                marginBottom: 10,
                border: "1px solid #ccc",
                borderRadius: 6,
                overflow: "hidden",
              }}
            >
              <button
                type="button"
                onClick={() => {
                  setItems((prev) =>
                    prev.map((it) =>
                      it.kind === "tool" && it.toolCallId === item.toolCallId
                        ? { ...it, expanded: !it.expanded }
                        : it,
                    ),
                  );
                }}
                style={{
                  width: "100%",
                  textAlign: "left",
                  padding: "8px 10px",
                  background: "#eef6ee",
                  border: "none",
                  cursor: "pointer",
                  fontFamily: "inherit",
                }}
              >
                <span style={{ fontWeight: 600 }}>{item.toolName}</span>
                <span className="cec-tool-id" style={{ color: "#666", fontSize: "0.8rem", marginLeft: 8 }}>
                  {item.toolCallId}
                </span>
              </button>
              {item.expanded && (
                <pre
                  style={{
                    margin: 0,
                    padding: 10,
                    fontSize: "0.82rem",
                    background: "#1e1e1e",
                    color: "#d4d4d4",
                    maxHeight: 280,
                    overflow: "auto",
                  }}
                >
                  {item.content ?? "… waiting for result"}
                </pre>
              )}
            </div>
          );
        })}
        <div ref={streamEndRef} />
      </div>

      <div className="cec-input-row" style={{ display: "flex", gap: 8, maxWidth: "100%" }}>
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="ask the card engine agent…"
          rows={3}
          disabled={loading}
          style={{
            flex: 1,
            minWidth: 0,
            resize: "vertical",
            padding: 10,
            borderRadius: 6,
            border: "1px solid #ccc",
            fontFamily: "inherit",
            boxSizing: "border-box",
          }}
        />
        <button
          type="button"
          onClick={() => void send()}
          disabled={loading || !input.trim()}
          style={{
            alignSelf: "flex-end",
            padding: "12px 20px",
            borderRadius: 6,
            border: "none",
            background: loading ? "#aaa" : "#3d6",
            color: "#fff",
            cursor: loading ? "default" : "pointer",
            fontWeight: 600,
            flexShrink: 0,
          }}
        >
          {loading ? "…" : "send"}
        </button>
      </div>
    </div>
    </>
  );
};

export default CardEngineChat;
